/**
 * ChangeOrdersStep — pay app editor step 2 (change order log + billing traceability).
 */

import { forwardRef, useImperativeHandle, useRef, useState, FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createChangeOrder, deleteChangeOrder } from '../../../api/payApps'
import CoAttachmentField, { type CoAttachmentRow } from './CoAttachmentField'
import { isGenericCoDescription } from '../../../lib/coLabel'
import { fetchChangeOrderTrace, type ChangeOrderTraceRow } from '../../../api/projects'
import SavedBadge from '../../../components/SavedBadge'
import { useActionToast } from '../../../lib/useActionToast'
import MoneyInput from '../../../components/MoneyInput'

interface CO {
  id: number
  co_number: number
  description: string
  amount: string
  status: string
}

export interface ChangeOrdersStepHandle {
  /** Save pending entry or validate before leaving step 2. Returns true if safe to advance. */
  prepareAdvance: () => Promise<boolean>
}

interface Props {
  payAppId: number
  projectId: number
  changeOrders: CO[]
  attachments?: CoAttachmentRow[]
  defaultRetainagePct?: number | string | null
  onRefresh: () => void
  onOpenPayApp?: (payAppId: number) => void
}

const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 } as const
const fld = { width: '100%', padding: '8px 10px', fontSize: 13 } as const

function fmtMoney(raw: string | number | null | undefined): string {
  const n = typeof raw === 'number' ? raw : parseFloat(raw ?? '0')
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function parseAmount(raw: string): number {
  return parseFloat(raw.replace(/[^0-9.]/g, '')) || 0
}

function hasText(value: string): boolean {
  return value.trim().length > 0
}

function hasAmount(value: string): boolean {
  return value.replace(/[^0-9.]/g, '').trim().length > 0
}

function billingChip(row: ChangeOrderTraceRow | undefined): { label: string; cls: string } {
  if (!row) return { label: 'This pay app', cls: 'c-blue' }
  if (row.billing_status === 'billed') return { label: `Billed · PA #${row.app_number}`, cls: 'c-grn' }
  if (row.billing_status === 'void') return { label: 'Void', cls: 'c-gry' }
  return { label: 'Not yet billed', cls: 'c-amb' }
}

const ChangeOrdersStep = forwardRef<ChangeOrdersStepHandle, Props>(function ChangeOrdersStep(
  { payAppId, projectId, changeOrders, attachments = [], defaultRetainagePct = 5, onRefresh, onOpenPayApp },
  ref,
) {
  const { showToast, Toast } = useActionToast()
  const saveInFlight = useRef(false)
  const [desc, setDesc] = useState('')
  const [amount, setAmount] = useState('')
  const [holdRetainage, setHoldRetainage] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [lastSavedLabel, setLastSavedLabel] = useState<string | null>(null)

  const { data: trace = [] } = useQuery({
    queryKey: ['co-trace', projectId],
    queryFn: () => fetchChangeOrderTrace(projectId),
    staleTime: 15_000,
  })

  const traceByCoId = new Map(trace.map(t => [t.id, t]))
  const unbilledElsewhere = trace.filter(
    t => t.billing_status === 'not_yet_billed' && t.pay_app_id !== payAppId,
  )

  const total = changeOrders.reduce((s, co) => s + (parseFloat(co.amount) || 0), 0)
  const entryReady = hasText(desc) && hasAmount(amount)

  async function savePendingEntry(): Promise<boolean> {
    if (!entryReady) return true
    if (saveInFlight.current || busy) return false

    saveInFlight.current = true
    setBusy(true)
    setErr(null)
    const label = `${desc.trim()} · ${fmtMoney(parseAmount(amount))}`
    try {
      await createChangeOrder(payAppId, {
        description: desc.trim(),
        amount: parseAmount(amount),
        hold_retainage: holdRetainage,
      })
      setDesc('')
      setAmount('')
      setHoldRetainage(false)
      setLastSavedLabel(label)
      showToast('Change order saved')
      onRefresh()
      return true
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Failed to add change order')
      return false
    } finally {
      setBusy(false)
      saveInFlight.current = false
    }
  }

  async function commitIfComplete(): Promise<void> {
    if (saveInFlight.current || busy) return
    if (!entryReady) return
    await savePendingEntry()
  }

  async function handleFieldBlur(_field: 'desc' | 'amount') {
    await commitIfComplete()
  }

  function handleFieldKeyDown(e: React.KeyboardEvent, field: 'desc' | 'amount') {
    if (e.key === 'Enter') {
      e.preventDefault()
      void commitIfComplete()
      return
    }
    if (e.key === 'Tab' && field === 'amount' && !e.shiftKey) {
      void commitIfComplete()
    }
  }

  useImperativeHandle(ref, () => ({
    async prepareAdvance() {
      const descFilled = hasText(desc)
      const amountFilled = hasAmount(amount)

      if (!descFilled && !amountFilled) return true

      if (descFilled !== amountFilled) {
        setErr('Finish or clear the change order entry')
        return false
      }

      return savePendingEntry()
    },
  }), [desc, amount, holdRetainage, busy, payAppId, onRefresh])

  async function handleAdd(e: FormEvent) {
    e.preventDefault()
    if (!entryReady) return
    await savePendingEntry()
  }

  async function handleDelete(id: number) {
    if (!confirm('Delete this change order?')) return
    setErr(null)
    try {
      await deleteChangeOrder(id)
      onRefresh()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Delete failed')
    }
  }

  return (
    <div className="panel" style={{ padding: 14 }} data-testid="change-orders-step">
      {Toast}
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>
        Change Order Log
      </div>

      {unbilledElsewhere.length > 0 && (
        <div style={{
          marginBottom: 14, padding: '10px 12px', borderRadius: 6,
          background: 'var(--amber-l)', border: '1px solid var(--amber)', fontSize: 12,
        }} data-testid="co-unbilled-warning">
          <strong>{unbilledElsewhere.length} change order{unbilledElsewhere.length !== 1 ? 's' : ''} not yet billed</strong>
          {' '}on other pay apps — review before submitting.
        </div>
      )}

      {changeOrders.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--ink4)', marginBottom: 14 }}>No change orders on this pay app yet.</p>
      ) : (
        <div style={{ marginBottom: 14 }}>
          {changeOrders.map((co, index) => {
            const row = traceByCoId.get(co.id)
            const chip = billingChip(row)
            return (
            <div
              key={co.id}
              data-testid={`co-row-${co.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                marginBottom: 8,
                border: '1px solid var(--line)',
                borderRadius: 6,
                background: 'var(--bg2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--ink3)',
                    background: 'var(--line)',
                    padding: '2px 6px',
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                  data-testid={`co-label-${co.id}`}
                >
                  {`CO #${co.co_number ?? (index + 1)}`}
                </span>
                <CoAttachmentField
                  chipsOnly
                  projectId={projectId}
                  changeOrderId={co.id}
                  attachments={attachments.filter((a) => Number(a.change_order_id) === co.id)}
                  onUploaded={onRefresh}
                />
                {!isGenericCoDescription(co.co_number ?? (index + 1), co.description) && (
                  <span style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {co.description}
                  </span>
                )}
                <button
                  type="button"
                  className={`chip ${chip.cls}`}
                  style={{ cursor: row && onOpenPayApp ? 'pointer' : 'default', border: 'none', flexShrink: 0 }}
                  data-testid={`co-billing-${co.id}`}
                  onClick={() => row && onOpenPayApp?.(row.pay_app_id)}
                  title={row ? `Open PA #${row.app_number}` : undefined}
                >
                  {chip.label}
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span className="mono-c" style={{ fontSize: 13, fontWeight: 600, color: 'var(--blue)' }}>
                  {fmtMoney(co.amount)}
                </span>
                <CoAttachmentField
                  hideChips
                  projectId={projectId}
                  changeOrderId={co.id}
                  attachments={attachments.filter((a) => Number(a.change_order_id) === co.id)}
                  onUploaded={onRefresh}
                />
                <button
                  type="button"
                  onClick={() => handleDelete(co.id)}
                  title="Delete"
                  aria-label="Delete change order"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--red)' }}
                >
                  🗑
                </button>
              </div>
            </div>
            )
          })}
          <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--ink)', paddingTop: 8, borderTop: '1px solid var(--line)' }}>
            Contract adjustment: +<span className="mono-c">{fmtMoney(total)}</span>
          </div>
        </div>
      )}

      {lastSavedLabel && !entryReady && (
        <div
          style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}
          data-testid="co-entry-saved"
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{lastSavedLabel}</span>
          <SavedBadge testId="co-entry-saved-badge" />
          <button
            type="button"
            className="btn b-ol btn-sm"
            data-testid="co-entry-edit"
            onClick={() => setLastSavedLabel(null)}
          >
            Edit
          </button>
        </div>
      )}

      <form onSubmit={handleAdd} style={{ borderTop: changeOrders.length ? '1px solid var(--line)' : undefined, paddingTop: changeOrders.length ? 14 : 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10, marginBottom: 10 }}>
          <label>
            <span style={lbl}>Description</span>
            <input
              className="fld-i"
              style={fld}
              value={desc}
              onChange={e => {
                setDesc(e.target.value)
                setLastSavedLabel(null)
              }}
              onBlur={() => { void handleFieldBlur('desc') }}
              onKeyDown={e => handleFieldKeyDown(e, 'desc')}
              placeholder="Change order description"
            />
          </label>
          <label>
            <span style={lbl}>Amount</span>
            <MoneyInput
              className="fld-i"
              style={fld}
              value={amount}
              onChange={v => {
                setAmount(v)
                setLastSavedLabel(null)
              }}
              onBlur={() => { void handleFieldBlur('amount') }}
              onKeyDown={e => handleFieldKeyDown(e, 'amount')}
              placeholder="0.00"
            />
          </label>
        </div>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 12, color: 'var(--ink2)', cursor: 'pointer' }}
          data-testid="co-hold-retainage-label"
        >
          <input
            type="checkbox"
            checked={holdRetainage}
            onChange={e => setHoldRetainage(e.target.checked)}
            data-testid="co-hold-retainage"
          />
          Hold retainage on this CO ({parseFloat(String(defaultRetainagePct)) || 5}%)
        </label>
        <button
          type="submit"
          className="btn b-dk"
          data-testid="co-entry-save"
          disabled={busy || !entryReady}
        >
          {busy ? 'Saving…' : 'Add C/O'}
        </button>
      </form>

      {err && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{err}</p>}
    </div>
  )
})

export default ChangeOrdersStep
